import { orgAPI } from "@/api/api";
import { IntegrationConnection } from "@cohesive/api-client";
import { GithubRepoEntity } from "@cohesive/api-entities";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { buildGenericById, toGenericArray } from "../../genericProjection";

export function useGitHubReposList(connection: IntegrationConnection) {
  const infiniteQuery = useInfiniteQuery({
    queryKey: ["github-repositories-list", connection?.id || ""],
    queryFn: ({ pageParam = {} }) => {
      return orgAPI.toolbox.execute<GithubRepoEntity[]>({
        integration_connection_id: connection?.id || "",
        action_name: "github-repositories-list",
        action_input: {
          sort: "updated",
          affiliation: "owner,collaborator,organization_member", // Include all accessible repositories
          per_page: 100, // Maximum allowed by GitHub API
          ...pageParam, // Spread pagination parameters (e.g., { page: 2 })
        },
        // Typed repos drive the rich row UI; their generic projections ride
        // along in data_generic and are what get placed on the canvas.
        entity_format: "both",
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination?.next;
    },
    initialPageParam: {},
    enabled: !!connection?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes (repositories don't change often)
  });

  // Flatten all pages into a single array for easier consumption
  const data = useMemo(() => {
    if (!infiniteQuery.data) return undefined;

    return {
      data: infiniteQuery.data.pages.flatMap((page) => page.data),
      pagination:
        infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1]
          ?.pagination,
    };
  }, [infiniteQuery.data]);

  // id→generic lookup for placement. The flattened `data` above drops
  // data_generic, so expose the map separately rather than reshaping `data`.
  const genericById = useMemo(
    () =>
      buildGenericById(
        infiniteQuery.data?.pages.flatMap((page) =>
          toGenericArray(page.data_generic),
        ) ?? [],
      ),
    [infiniteQuery.data],
  );

  return {
    ...infiniteQuery,
    data,
    genericById,
  };
}
